import { useQuery } from "@tanstack/react-query";
import type { RedKeel } from "../config/networks";
import type { Address } from "viem";

/**
 * HISTÓRICO de precio de un pool, para el gráfico del create. Lo publica `indexer-precios.py` como
 * `precio.<clave>.<pool>.json` (velas OHLC horarias reconstruidas de los eventos Swap). Los precios
 * están en price0in1 (token1 por token0); quien lo pinta decide la orientación. Si el fichero aún no
 * existe —pool recién listado, agregador sin backfill— devuelve `null` y el gráfico lo dice, sin
 * inventar una línea plana.
 */
export type Vela = { t: number; o: number; h: number; l: number; c: number };
export type Hist = { pool: string; dec0: number; dec1: number; res: number; velas: Vela[] };

export function useHistPrecio(red: RedKeel, pool?: Address, activo = true) {
  return useQuery({
    queryKey: ["hist-precio", red.clave, pool?.toLowerCase()],
    enabled: activo && !!pool,
    // Mientras el fichero aún no tiene velas —pool recién listado, agregador en pleno backfill— se
    // sondea cada 15 s para que el gráfico aparezca en cuanto haya datos; ya con datos, se relaja a
    // 5 min. Sin esto, un `null` inicial quedaba cacheado y la pantalla decía "no hay histórico"
    // aunque el backend ya lo hubiera reconstruido.
    staleTime: 10_000,
    refetchInterval: (q) => (q.state.data ? 300_000 : 15_000),
    queryFn: async (): Promise<Hist | null> => {
      try {
        const r = await fetch(`/precio.${red.clave}.${pool!.toLowerCase()}.json?t=${Math.floor(Date.now() / 300000)}`);
        if (!r.ok) return null;
        const d = await r.json();
        const velas: Vela[] = Array.isArray(d.candles)
          ? d.candles
              .map((v: number[]) => ({ t: v[0]!, o: v[1]!, h: v[2]!, l: v[3]!, c: v[4]! }))
              .filter((v: Vela) => Number.isFinite(v.c) && v.c > 0)
          : [];
        if (!velas.length) return null;
        return { pool: d.pool, dec0: d.dec0, dec1: d.dec1, res: d.res, velas };
      } catch {
        return null;
      }
    },
  });
}
